import { formatCpuSpeed, formatRam } from '@/lib/formatAgent'

function asObject(details) {
  if (!details || typeof details !== 'object' || Array.isArray(details)) return null
  return details
}

const COVERED_INVENTORY_NAMES = new Set([
  'Processor',
  'RAM',
  'System',
  'BIOS',
  'MAC addresses',
])

/** Single-line CPU summary for the Hardware panel (name · cores · speed). */
export function formatCpuSummaryLine(agent) {
  if (!agent) return null
  const parts = []
  if (agent.cpu_name) parts.push(agent.cpu_name)
  if (agent.cpu_cores != null && agent.cpu_cores !== '') {
    parts.push(`${agent.cpu_cores} cores`)
  }
  if (agent.cpu_speed_mhz) {
    parts.push(formatCpuSpeed(agent.cpu_speed_mhz))
  }
  return parts.length ? parts.join(' · ') : null
}

export function findHardwareByName(hardware = [], name) {
  return hardware.find((row) => row?.name === name) ?? null
}

function normaliseDriveLetter(value) {
  if (value == null || value === '') return null
  return String(value).replace(/:\\?$/, '').replace(/:$/, '').toUpperCase()
}

/** Rows already shown as key specs / identity — hide from Other components. */
export function isCoveredHardwareRow(row, systemDrive) {
  if (!row) return true
  if (COVERED_INVENTORY_NAMES.has(row.name)) return true

  const drive = normaliseDriveLetter(systemDrive)
  if (drive && (row.category === 'disk' || /^[A-Za-z]:?$/.test(String(row.name || '')))) {
    const rowDrive = normaliseDriveLetter(row.name)
    if (rowDrive && rowDrive === drive) return true
  }
  return false
}

export function filterOtherHardwareComponents(hardware = [], systemDrive) {
  return hardware.filter((row) => !isCoveredHardwareRow(row, systemDrive))
}

/** Manufacturer column: use stored value or infer from Atera details JSON. */
export function formatHardwareManufacturer(row) {
  if (row.manufacturer) return row.manufacturer
  const d = asObject(row.details)
  if (!d) return '—'
  if (row.name === 'BIOS') return d.BiosManufacturer || '—'
  if (row.name === 'System') return d.Vendor || '—'
  return '—'
}

/** Details column: model field or formatted payload (RAM MB, disks, MACs, etc.). */
export function formatHardwareDetails(row) {
  if (row.name === 'RAM') {
    const mb = asObject(row.details)?.mb
    return formatRam(mb)
  }

  if (row.model) {
    if (row.name === 'Processor') {
      const d = asObject(row.details)
      const extra = []
      if (d?.cores) extra.push(`${d.cores} cores`)
      if (d?.clock) extra.push(`${Number(d.clock).toFixed(2)} GHz`)
      return extra.length ? `${row.model} · ${extra.join(' · ')}` : row.model
    }
    return row.model
  }

  const d = asObject(row.details)
  if (!d) return '—'

  if (row.name === 'BIOS') {
    const parts = [d.BiosVersion, d.BiosReleaseDate && formatShortDate(d.BiosReleaseDate)]
    return parts.filter(Boolean).join(' · ') || '—'
  }

  if (row.name === 'System') {
    return [d.VendorBrandModel, d.VendorSerialNumber].filter(Boolean).join(' · ') || '—'
  }

  if (row.name === 'MAC addresses' && d.mac_addresses) {
    const macs = Array.isArray(d.mac_addresses) ? d.mac_addresses : [d.mac_addresses]
    return macs.join(', ')
  }

  if (row.category === 'disk' && d.Total != null) {
    const total = Number(d.Total)
    const free = Number(d.Free)
    if (!Number.isNaN(total) && total > 0) {
      const toGb = (mb) => `${(mb / 1024).toFixed(1)} GB`
      if (!Number.isNaN(free)) {
        return `${toGb(free)} free of ${toGb(total)}`
      }
      return toGb(total)
    }
  }

  if (row.name === 'Processor') {
    const parts = []
    if (d.cores) parts.push(`${d.cores} cores`)
    if (d.clock) parts.push(`${Number(d.clock).toFixed(2)} GHz`)
    return parts.join(' · ') || '—'
  }

  return '—'
}

function formatShortDate(iso) {
  try {
    return new Date(iso).toLocaleDateString('en-GB')
  } catch {
    return iso
  }
}
